import { Aircraft, JobAssociableAssignmentSummary, Maybe, Ticket, TicketLineItem, TicketLocation, TicketPax } from "../sharedCode/avroGraphql";
import Big from "big.js";
import { BELL_212 } from "./FuelCalculator";
import { ExtendTicket, ExtendTicketLineItem, JobExtended, TicketExtended, TicketLocationExtended } from "./TypeExtensions";
import { DefinitelyAnArray, HasValue } from "../sharedCode/HelperMethods";
import sum from "hash-sum";
import { EMERGENCY_CONTACT_ASSIGNABLE_ID, MIN_HELI, MIN_PILOT, MINIMUM_LINE_ITEM_TYPES, QUANTITY_TYPE_AMOUNT } from "../sharedCode/metadata";
import dayjs from "dayjs";
import { useMediaQuery } from "@mui/material";
import { Theme } from "@mui/system";
import { INITIALIZATION_TICKET_ID } from "../AppContainer";

export default {};

export function IsEngineerRequired(aircraft: Aircraft | undefined): boolean {
    return (aircraft?.AircraftType.name ?? "") === BELL_212;
}

export function IsNullOrEmpty(target: string | null | Maybe<string> | undefined): boolean {
    return target === "" || target == null;
}

export function IsValidNumber(possibleNumber: Maybe<number> | string | undefined, precision: number, allowNegatives = false): boolean {
    let result = true;

    try {
        const discard = new Big(possibleNumber ?? 0);

        if (!allowNegatives) {
            if (discard.lt(0)) {
                result = false;
            }
        }

        const numberParts = (possibleNumber?.toLocaleString() ?? "").split(".");
        const decimalPlaces = numberParts.length > 1 ? numberParts[1].length : 0;
        if (decimalPlaces > precision) {
            result = false;
        }

        if (numberParts.length > 1 && precision === 0) {
            result = false;
        }
    } catch {
        if (!(possibleNumber === "" || possibleNumber === ".")) {
            result = false;
        }
    } finally {
        // fricking eslint
    }

    return result;
}

export function FilterLineItems(ticket: TicketExtended, typeList: number[]): Array<TicketLineItem> {
    return DefinitelyAnArray(ticket.LineItems).filter((lineItem) => typeList.findIndex((id) => lineItem && id === lineItem.line_item_type_id) >= 0);
}

export function formatNumericInput(valueToFormat: number | null | undefined, precision: number): string {
    return valueToFormat === undefined || valueToFormat === null || valueToFormat === 0 ? "" : valueToFormat.toFixed(precision);
}

export function isValidEmail(valueToValidate: string): boolean {
    const re = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
    return re.test(valueToValidate);
}

export function optionalEss(count: number): string {
    return count === 1 ? "" : "S";
}

export function isDevelopmentEnvironment(): boolean {
    return process.env.NODE_ENV === "development" || window.location.hostname.startsWith("192.168.1.") || window.location.hostname.includes("localhost");
}

export function isProductionEnvironment(): boolean {
    return !isDevelopmentEnvironment();
}

export function calculateLookupKeyHash(company_id: Maybe<number>, flightLeg: TicketLocationExtended | Maybe<TicketLocation> | undefined): string | undefined {
    if (company_id != null && flightLeg != null && flightLeg.location != null && flightLeg.location.trim() != "") {
        return calculateLocationKeyHash(company_id, flightLeg.location);
    }
    return undefined;
}
export function calculateLocationKeyHash(company_id: Maybe<number>, location: Maybe<string> | undefined): string | undefined {
    if (company_id != null && location != null && location.trim() != "") {
        const lookup = { company_id, location: location.trim() };
        const hash = sum(lookup);
        return hash;
    }
    return undefined;
}

export function getTicketValuesFromJob(newTicket: TicketExtended, selectedJob: JobExtended, pilotId: number): Record<string, unknown> {
    let nextLocalId = newTicket.nextLocalId;
    const emergencyContact = selectedJob.JobContacts?.find((c) => {
        return c?.assignable_resource_type_id === EMERGENCY_CONTACT_ASSIGNABLE_ID;
    });

    const assignmentsForDay = getPilotAssignmentsByDate(DefinitelyAnArray(selectedJob.JobAssignments), newTicket.date);

    let staffAssignments: Record<string, unknown> = {};
    assignmentsForDay.forEach((a) => {
        if (a.assigned_role === "PILOT") {
            if (a.assigned_person_id === pilotId) {
                staffAssignments = { ...staffAssignments, pilot_person_id: a.assigned_person_id };
            } else {
                staffAssignments = { ...staffAssignments, pilot_person_id2: a.assigned_person_id };
            }
        }
        if (a.assigned_role === "AME") {
            staffAssignments = { ...staffAssignments, ame_person_id: a.assigned_person_id };
        }
    });

    const ticketPax = selectedJob.JobPax?.map((jp) => {
        return {
            id: nextLocalId--,
            name: HasValue(jp?.first_name) ? `${jp?.first_name} ${jp?.last_name}` : "",
            ticket_id: newTicket.id,
        } as TicketPax;
    });

    // only keep the non-minimum rows first, then add back any mins defined on the job
    const lineItems = DefinitelyAnArray(newTicket.LineItems)
        .filter((r) => MINIMUM_LINE_ITEM_TYPES.findIndex((id) => r && id === r.line_item_type_id) < 0)
        .slice();

    if (selectedJob.aircraft_minimum) {
        lineItems.push(
            ExtendTicketLineItem({
                id: nextLocalId--,
                line_item_type_id: MIN_HELI,
                quantity_type_id: QUANTITY_TYPE_AMOUNT,
                amount: selectedJob.aircraft_minimum,
            } as TicketLineItem)
        );
    }

    if (selectedJob.pilot_minimum) {
        lineItems.push(
            ExtendTicketLineItem({
                id: nextLocalId--,
                line_item_type_id: MIN_PILOT,
                quantity_type_id: QUANTITY_TYPE_AMOUNT,
                amount: selectedJob.pilot_minimum,
            } as TicketLineItem)
        );
    }

    const ticketDayjs = dayjs(newTicket.date);
    const dayAircraft = selectedJob.JobAircraft?.find((a) => ticketDayjs.isBetween(a?.start_date, a?.end_date, "day", "[]"));

    return {
        nextLocalId: nextLocalId,
        ...staffAssignments,
        job_id: selectedJob.id,
        company_id: selectedJob.company_id,
        customer_po: selectedJob.customer_po,
        aircraft_id: dayAircraft?.aircraft_id,
        airport_code: selectedJob.base_airport_code,
        contact_name: emergencyContact?.contact_person,
        contact_email: emergencyContact?.email?.toLowerCase(),
        contact_phone: emergencyContact?.phone,
        Pax: ticketPax,
        LineItems: lineItems,
    };
}

export function getPilotAssignmentsByDate(list: JobAssociableAssignmentSummary[], date: string): JobAssociableAssignmentSummary[] {
    const searchDate = dayjs(date).utcOffset(0);
    if (!list || !HasValue(date) || !searchDate.isValid()) {
        return [];
    }

    return list.filter((i: JobAssociableAssignmentSummary) => {
        return searchDate.isBetween(i.assignment_begin, i.assignment_end, "day", "[]");
    });
}

export function isLandscape(theme: Theme): boolean {
    return useMediaQuery(theme.breakpoints.up("md"));
}

export function createInitializationTicket(): TicketExtended {
    return ExtendTicket({ id: INITIALIZATION_TICKET_ID } as Ticket);
}

export function isInitializationTicket(ticket: TicketExtended): boolean {
    return ticket.id === INITIALIZATION_TICKET_ID;
}

export function isNotInitializationTicket(ticket: TicketExtended): boolean {
    return !isInitializationTicket(ticket);
}

export function getTicketById(ticketList: TicketExtended[], ticketId: number): TicketExtended {
    const result = ticketList.find((a: TicketExtended) => {
        return a.id === ticketId;
    });

    if (!result) {
        return createInitializationTicket();
    }

    return result;
}
